/**
 * Pure helpers for the attendance screens: period choice, sorting, percent formatting and the CSV
 * export (UTF-8 BOM + ';' separators so Excel with Icelandic settings opens þ/ð/æ correctly).
 */
import type { Section } from '../../../../shared/audience';
import { STATS_MAX_RANGE_DAYS, seasonRange, type DateRange, type MemberAttendanceDTO } from '../../../../shared/dto/stats';
import { DAY_MS, parseDateStr, toDateStr } from '../../../../shared/time';

export type RangeMode = 'this' | 'last' | 'custom';
export type MemberSort = 'name' | 'pct';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRealDate(d: string): boolean {
  if (!DATE_RE.test(d)) return false;
  const ms = parseDateStr(d);
  // "2026-13-45" passes the regex but parses to NaN; toDateStr(NaN) would throw.
  return Number.isFinite(ms) && toDateStr(ms) === d;
}

/** A custom range the server accepts: real dates, from ≤ to, at most STATS_MAX_RANGE_DAYS days. */
export function isValidRange(r: DateRange): boolean {
  if (!isRealDate(r.from) || !isRealDate(r.to) || r.to < r.from) return false;
  return (parseDateStr(r.to) - parseDateStr(r.from)) / DAY_MS + 1 <= STATS_MAX_RANGE_DAYS;
}

/** The range to query for a period choice; null when the custom range is invalid. */
export function rangeFor(mode: RangeMode, today: string, custom: DateRange): DateRange | null {
  if (mode === 'this') return seasonRange(today);
  if (mode === 'last') return seasonRange(today, -1);
  return isValidRange(custom) ? custom : null;
}

/** "2026–2027" for a season range (or any range spanning years). */
export function rangeYears(r: DateRange): string {
  const a = r.from.slice(0, 4);
  const b = r.to.slice(0, 4);
  return a === b ? a : `${a}–${b}`;
}

/** Member names are Icelandic: sort them with Icelandic collation whatever the UI language (Þ, Æ, Ö last). */
export const nameCollator = new Intl.Collator('is', { sensitivity: 'base' });

/** Name order (Icelandic collation) or attendance (highest first, no data last, then name). */
export function sortMembers(list: readonly MemberAttendanceDTO[], by: MemberSort, collator: Intl.Collator): MemberAttendanceDTO[] {
  const byName = (a: MemberAttendanceDTO, b: MemberAttendanceDTO) => collator.compare(a.fullName, b.fullName);
  const sorted = [...list];
  if (by === 'name') return sorted.sort(byName);
  return sorted.sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || b.total - a.total || byName(a, b));
}

export function filterBySection(list: readonly MemberAttendanceDTO[], section: Section | null): MemberAttendanceDTO[] {
  return section ? list.filter((m) => m.section === section) : [...list];
}

const pctFormats = new Map<string, Intl.NumberFormat>();
function pctFormat(loc: string, digits: number): Intl.NumberFormat {
  const key = `${loc}|${digits}`;
  let f = pctFormats.get(key);
  if (!f) {
    f = new Intl.NumberFormat(loc, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: 0 });
    pctFormats.set(key, f);
  }
  return f;
}

/** 87.5 → "88%" (or "87,5%" with digits = 1); null → the dash `none`. */
export function formatPct(pct: number | null, loc: string, none = '–', digits = 0): string {
  return pct === null ? none : pctFormat(loc, digits).format(pct / 100);
}

/** Bar width in percent, clamped (0 % still shows as an empty track). */
export function barWidth(pct: number | null): number {
  return pct === null ? 0 : Math.max(0, Math.min(100, pct));
}

/* ─── CSV ───────────────────────────────────────────────────────────────── */

/** UTF-8 byte order mark: Excel then reads the file as UTF-8. Escaped so no editor strips it. */
export const CSV_BOM = '\uFEFF';
export const CSV_SEP = ';';

/**
 * One CSV cell: quoted when it holds the separator, a quote, a line break or edge spaces; a leading
 * = + - @ (formula in Excel) is neutralised with an apostrophe.
 */
export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[";\r\n]|^\s|\s$/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Rows → CSV text with BOM and CRLF line endings. */
export function toCsv(rows: readonly (readonly (string | number | null | undefined)[])[]): string {
  return CSV_BOM + rows.map((r) => r.map(csvCell).join(CSV_SEP)).join('\r\n') + '\r\n';
}

/** Percent for spreadsheets: one decimal with the locale's decimal mark, no % sign ("33,3"). */
export function csvPct(pct: number | null, loc: string): string {
  return pct === null ? '' : new Intl.NumberFormat(loc, { maximumFractionDigits: 1, useGrouping: false }).format(pct);
}

export interface CsvLabels {
  name: string;
  section: string;
  present: string;
  excused: string;
  absent: string;
  total: string;
  pct: string;
  sectionName: (s: Section | null) => string;
}

/** The member table (name order) as CSV. */
export function membersCsv(list: readonly MemberAttendanceDTO[], labels: CsvLabels, loc: string, collator: Intl.Collator): string {
  const rows = sortMembers(list, 'name', collator).map((m) => [
    m.fullName,
    labels.sectionName(m.section),
    m.present,
    m.excused,
    m.absent,
    m.total,
    csvPct(m.pct, loc),
  ]);
  return toCsv([[labels.name, labels.section, labels.present, labels.excused, labels.absent, labels.total, labels.pct], ...rows]);
}

/** ASCII file name for the export: maeting-2026-09-01-2027-08-31.csv */
export function csvFileName(r: DateRange): string {
  return `maeting-${r.from}-${r.to}.csv`;
}

export type SaveResult = 'downloaded' | 'shared' | 'cancelled';

/** Touch devices (iPad/iPhone) and installed apps: blob downloads are unreliable there (iOS home-screen apps). */
function prefersShareSheet(): boolean {
  try {
    return matchMedia('(pointer: coarse)').matches || matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/** Save a Blob as a download link (object URL revoked after a minute). */
function anchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Hand a file to the user. On iPad/iPhone (and installed apps) the system share sheet is used —
 * "Vista í Skrár" / mail / AirDrop — because `<a download>` with a blob often does nothing in an
 * iOS home-screen app. Elsewhere, or when sharing files is not supported or fails, a normal
 * download. Must be called from a click handler (the share sheet needs the user gesture).
 */
export async function saveFile(blob: Blob, filename: string): Promise<SaveResult> {
  if (prefersShareSheet() && typeof File === 'function' && typeof navigator.share === 'function') {
    // Share targets match on the bare MIME type: drop parameters such as ;charset=utf-8.
    const file = new File([blob], filename, { type: blob.type.split(';')[0] || 'application/octet-stream' });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return 'shared';
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
        // NotAllowedError / unsupported type: fall back to the download link below.
      }
    }
  }
  anchorDownload(blob, filename);
  return 'downloaded';
}
